"use client";

import { TimeFrame } from "charts/src/types/chart";
import get from "lodash/get";
import { useMemo } from "react";

import { EmptyView } from "@/components/empty-view";
import { BlockedView } from "@/features/analysis/components/BlockedView";
import { REVENUE_BREAKDOWN_CHART_ID } from "@/features/analysis/components/key-metrices-charts/data/chart-config";
import { MockChartGridItem } from "@/features/analysis/components/mock/financial-metrics/MockChartGridItem";
import {
  ChartConfig,
  ChartConfigListPaths,
  useChartConfigList,
} from "@/features/analysis/services/chart-config-list";
import { Lang } from "@/i18n-config";
import {
  RevenueBreakdownConfigType,
  getRevenueBreakdownConfigSchema,
  usePublicData,
} from "@/services/public-data";
import { DateOption } from "@/utils/date-options";
import { getTranslations } from "@/utils/i18n/get-translations";

import { LoadingIndicator } from "ui/core/button/LoadingIndicator";

import {
  timeFrameDateOptionLimitMap,
  timeFrameLimitMap,
} from "../data/chart-data-limits";
import { ChartGridItem } from "./ChartGridItem";

type ChartLayoutProps = {
  title?: string;
  symbol: string;
  lang: Lang;
  chartCategoryPath: ChartConfigListPaths;
  timeFrame: TimeFrame;
  dateOption?: DateOption;
  isBlocked: boolean;
};

export const ChartGridLayout = ({
  title,
  symbol,
  lang,
  chartCategoryPath,
  timeFrame,
  dateOption,
  isBlocked,
}: ChartLayoutProps) => {
  const { t } = getTranslations(lang);

  const { data: chartConfigs, isLoading } = useChartConfigList(symbol);

  const { data: { allowList } = { allowList: [] } } =
    usePublicData<RevenueBreakdownConfigType>({
      itemName: "revenue-breakdown-config",
      schema: getRevenueBreakdownConfigSchema,
    });

  const filteredConfig = useMemo(() => {
    const rawConfig = get(chartConfigs, chartCategoryPath, []);
    const config: ChartConfig[] = Array.isArray(rawConfig) ? rawConfig : [];

    // 某些 symbol 的 revenue breakdown 圖表資料有異常，因此不在 allowList 中，如果前端需要查看內容，可以在網址加上 #by-pass-allow-list 來 bypass
    const isByPassAllowList =
      typeof window !== "undefined" &&
      window.location.hash === "#by-pass-allow-list";

    const shouldShowRevenueBreakdown = (chartId: string) => {
      if (chartId !== REVENUE_BREAKDOWN_CHART_ID) return true;
      return isByPassAllowList || allowList.includes(symbol);
    };

    const isChartSupportTimeFrame = (timeFrameOptions: TimeFrame[]) => {
      return timeFrameOptions.includes(timeFrame);
    };

    return config
      .filter(({ disabled }) => !disabled)
      .filter(({ chartId }) => shouldShowRevenueBreakdown(chartId))
      .filter(({ timeframe: timeFrameOptions }) =>
        isChartSupportTimeFrame(timeFrameOptions)
      );
  }, [chartConfigs, chartCategoryPath, allowList, symbol, timeFrame]);

  const limit = useMemo(() => {
    return dateOption
      ? timeFrameDateOptionLimitMap[timeFrame][dateOption]
      : timeFrameLimitMap[timeFrame];
  }, [dateOption, timeFrame]);

  const isEmpty = filteredConfig.length === 0;

  if (isLoading) {
    return (
      <div className="flex h-[280px] items-center justify-center">
        <LoadingIndicator />
      </div>
    );
  }

  return (
    <>
      {title && <h3 className="mb-4">{title}</h3>}

      {isEmpty ? (
        <EmptyView className="h-[280px]">
          <p>{t.error.noData}</p>
        </EmptyView>
      ) : (
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
          {filteredConfig.map(({ chartId, slug, title }) => (
            <BlockedView
              key={chartId + timeFrame}
              isBlocked={isBlocked}
              real={
                <ChartGridItem
                  key={chartId + timeFrame}
                  lang={lang}
                  symbol={symbol}
                  chartId={chartId}
                  slug={slug}
                  timeFrame={timeFrame}
                  dateOption={dateOption}
                  limit={limit}
                />
              }
              mock={
                <MockChartGridItem
                  lang={lang}
                  symbol={symbol}
                  chartId={chartId}
                  slug={slug}
                  title={title}
                />
              }
            />
          ))}
        </div>
      )}
    </>
  );
};
